/**
 * Modal Cobrar Atencion - Diseño minimalista con colores corporativos
 *
 * Paleta: #1B4F72 (azul sidebar), #154360 (azul profundo), #E8F1F8 (fondo suave)
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { serviciosAPI, clientesAPI, atencionesAPI, comprobantesAtencionAPI } from '../services/api';

// Colores corporativos del sidebar
const BRAND = {
  primary: '#1B4F72',
  primaryDark: '#154360',
  text: '#2C3E50',
  muted: '#7F8C8D',
  border: '#E5E8EC',
  bgSoft: '#F8FAFC',
  bgSofter: '#F1F5F9',
  success: '#27AE60',
  warning: '#F39C12',
  danger: '#E74C3C'
};

const METODOS = [
  { id: 'efectivo', label: 'Efectivo', icon: 'bi-cash-coin' },
  { id: 'tarjeta', label: 'Tarjeta', icon: 'bi-credit-card-2-back' },
  { id: 'transferencia', label: 'Transferencia', icon: 'bi-bank' },
  { id: 'mixto', label: 'Mixto', icon: 'bi-wallet2' },
  { id: 'credito', label: 'Crédito', icon: 'bi-calendar-event' }
];

const CAT_LABEL = {
  consulta: 'Consulta',
  examen: 'Examen',
  procedimiento: 'Procedimiento',
  adaptacion_lc: 'Adaptación LC',
  otro: 'Otro'
};

// Props comunes para inputs de dinero/cantidad:
// - onWheel: previene que scroll del mouse cambie el valor (fix bug 40 → 40.01)
// - onKeyDown: previene que flechas arriba/abajo incrementen
const moneyInputProps = {
  onWheel: (e) => e.currentTarget.blur(),
  onKeyDown: (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
  }
};

const ModalCobrarAtencion = ({
  show,
  onClose,
  onSuccess,
  clientePreseleccionado = null,
  historiaClinicaId = null,
  optometristaId = null,
  serviciosPreseleccionados = null  // null o array de IDs
}) => {
  const [servicios, setServicios] = useState([]);
  const [loadingServicios, setLoadingServicios] = useState(false);
  const [busquedaServ, setBusquedaServ] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');

  const [cliente, setCliente] = useState(null);
  const [buscandoCliente, setBuscandoCliente] = useState('');
  const [clientesEncontrados, setClientesEncontrados] = useState([]);

  const [carrito, setCarrito] = useState([]);
  const [descuentoGlobal, setDescuentoGlobal] = useState(0);
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [montoRecibido, setMontoRecibido] = useState(0);
  const [pagoMixto, setPagoMixto] = useState([{ metodo: 'efectivo', monto: 0 }, { metodo: 'tarjeta', monto: 0 }]);
  const [tarjetaData, setTarjetaData] = useState({ codigo_autorizacion: '', ultimos_digitos: '', marca_tarjeta: '', tipo_tarjeta: 'credito' });

  const [esCredito, setEsCredito] = useState(false);
  const [abonoInicial, setAbonoInicial] = useState({ monto: 0, metodo_pago: 'efectivo' });
  const [fechaVencimiento, setFechaVencimiento] = useState('');

  const [notas, setNotas] = useState('');
  const [numeroOrden, setNumeroOrden] = useState(''); // [N° orden física] código manual de la orden de papel
  const [enviarEmail, setEnviarEmail] = useState(true);
  const [emailDestino, setEmailDestino] = useState('');
  const [guardando, setGuardando] = useState(false);

  // Estabilizar preseleccionados con ref para evitar loop (si el padre pasa un array nuevo en cada render)
  const presKey = useMemo(() => (serviciosPreseleccionados || []).join(','), [serviciosPreseleccionados]);
  const loadedRef = useRef(false);

  // Cargar servicios SOLO cuando abre el modal (no al cambiar props derivadas)
  useEffect(() => {
    if (!show) { loadedRef.current = false; return; }
    if (loadedRef.current) return;
    loadedRef.current = true;

    setCliente(clientePreseleccionado);
    setEmailDestino(clientePreseleccionado?.email || '');

    const load = async () => {
      try {
        setLoadingServicios(true);
        const res = await serviciosAPI.getAll({ activo: true, limit: 200 });
        if (res.data && res.data.success) {
          setServicios(res.data.data || []);
          const preIds = (serviciosPreseleccionados || []);
          if (preIds.length > 0) {
            const pre = preIds
              .map(pid => (res.data.data || []).find(s => s.id === pid))
              .filter(Boolean)
              .map(s => ({
                servicio_id: s.id,
                codigo: s.codigo,
                nombre: s.nombre,
                categoria: s.categoria,
                cantidad: 1,
                precio_unitario: parseFloat(s.precio),
                descuento_porcentaje: 0
              }));
            setCarrito(pre);
          }
        }
      } catch (e) {
        console.error(e);
        toast.error(e.response?.data?.message || 'Error al cargar servicios');
      } finally {
        setLoadingServicios(false);
      }
    };
    load();
    // eslint-disable-next-line
  }, [show, presKey]);

  const buscarClientes = async (q) => {
    setBuscandoCliente(q);
    if (!q || q.length < 2) { setClientesEncontrados([]); return; }
    try {
      const res = await clientesAPI.getAll({ buscar: q, limit: 5 });
      if (res.data.success) setClientesEncontrados(res.data.data);
    } catch (e) {}
  };

  const seleccionarCliente = (c) => {
    setCliente(c);
    setBuscandoCliente('');
    setClientesEncontrados([]);
    setEmailDestino(c.email || '');
  };

  const servFiltrados = useMemo(() => {
    return servicios.filter(s => {
      if (categoriaFiltro && s.categoria !== categoriaFiltro) return false;
      if (busquedaServ) {
        const q = busquedaServ.toLowerCase();
        return s.nombre.toLowerCase().includes(q) || s.codigo.toLowerCase().includes(q);
      }
      return true;
    });
  }, [servicios, categoriaFiltro, busquedaServ]);

  // FIX: setter funcional para evitar stale closure cuando la cajera hace clics rapidos
  // sobre tarjetas de servicio (varios clics consecutivos pueden perder estado intermedio
  // si se referencia `carrito` directamente desde la closure capturada).
  const agregarServicio = (s) => {
    setCarrito(prev => {
      const existe = prev.find(i => i.servicio_id === s.id);
      if (existe) {
        return prev.map(i => i.servicio_id === s.id ? { ...i, cantidad: i.cantidad + 1 } : i);
      }
      return [...prev, {
        servicio_id: s.id,
        codigo: s.codigo,
        nombre: s.nombre,
        categoria: s.categoria,
        cantidad: 1,
        precio_unitario: parseFloat(s.precio),
        descuento_porcentaje: 0
      }];
    });
  };

  const actualizarItem = (idx, campo, valor) => {
    setCarrito(prev => prev.map((it, i) => i === idx ? { ...it, [campo]: valor } : it));
  };

  const removerItem = (idx) => setCarrito(prev => prev.filter((_, i) => i !== idx));

  const totales = useMemo(() => {
    const subtotal = carrito.reduce((s, i) => {
      const bruto = i.cantidad * i.precio_unitario;
      const desc = bruto * (i.descuento_porcentaje || 0) / 100;
      return s + (bruto - desc);
    }, 0);
    const descG = parseFloat(descuentoGlobal || 0);
    const total = Math.max(0, subtotal - descG);
    return { subtotal, descuentoGlobal: descG, total };
  }, [carrito, descuentoGlobal]);

  const cambio = Math.max(0, parseFloat(montoRecibido || 0) - totales.total);

  // FIX UX: auto-fill monto recibido al total cuando metodo es efectivo y el monto es menor.
  // Cubre apertura con preseleccionados, agregado/eliminacion de servicios, cambio de descuento
  // y cambio de metodo a efectivo. Deps INTENCIONALMENTE excluyen montoRecibido para que NO
  // se dispare durante la captura manual (sino: usuario tipea "5" -> effect lo sobreescribe a "10").
  useEffect(() => {
    if (!esCredito && metodoPago === 'efectivo' && parseFloat(montoRecibido || 0) < totales.total && totales.total > 0) {
      setMontoRecibido(totales.total.toFixed(2));
    }
  }, [totales.total, metodoPago, esCredito]);

  const abrirPDF = async (comprobanteId) => {
    try {
      const res = await comprobantesAtencionAPI.descargarPDF(comprobanteId);
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      // Liberar el blob URL al minuto (ya se abrio en nueva pestaña)
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error('Error al abrir PDF'); }
  };

  const handleSubmit = async () => {
    if (carrito.length === 0) { toast.error('Agrega al menos un servicio'); return; }
    if (totales.total <= 0) { toast.error('El total debe ser > 0'); return; }
    if (!esCredito && metodoPago === 'efectivo' && parseFloat(montoRecibido || 0) < totales.total) {
      toast.error('Monto recibido menor al total'); return;
    }
    if (!esCredito && metodoPago === 'mixto') {
      const suma = pagoMixto.reduce((s, p) => s + parseFloat(p.monto || 0), 0);
      if (Math.abs(suma - totales.total) > 0.01) {
        toast.error(`Suma de pagos $${suma.toFixed(2)} no coincide con total $${totales.total.toFixed(2)}`); return;
      }
    }
    if (esCredito) {
      if (!abonoInicial.monto || parseFloat(abonoInicial.monto) <= 0) {
        toast.error('El abono inicial es requerido'); return;
      }
      if (parseFloat(abonoInicial.monto) >= totales.total) {
        toast.error('Abono inicial no puede ser >= total'); return;
      }
    }

    const payload = {
      cliente_id: cliente?.id || null,
      historia_clinica_id: historiaClinicaId,
      optometrista_id: optometristaId,
      items: carrito.map(i => ({
        servicio_id: i.servicio_id,
        cantidad: i.cantidad,
        precio_unitario: i.precio_unitario,
        descuento_porcentaje: parseFloat(i.descuento_porcentaje || 0)
      })),
      metodo_pago: esCredito ? 'credito' : metodoPago,
      monto_recibido: parseFloat(montoRecibido || 0),
      descuento_total: totales.descuentoGlobal,
      notas: notas || null,
      numero_orden: numeroOrden || null,
      enviar_email: enviarEmail && !!(emailDestino || cliente?.email)
    };

    if (esCredito) {
      payload.es_credito = true;
      payload.abono_inicial = { monto: parseFloat(abonoInicial.monto), metodo_pago: abonoInicial.metodo_pago };
      if (fechaVencimiento) payload.fecha_vencimiento_credito = fechaVencimiento;
    }

    if (!esCredito && metodoPago === 'mixto') {
      payload.pagos = pagoMixto.filter(p => parseFloat(p.monto || 0) > 0).map(p => ({
        metodo: p.metodo, monto: parseFloat(p.monto)
      }));
    }

    if (!esCredito && metodoPago === 'tarjeta') {
      Object.assign(payload, tarjetaData);
    }

    try {
      setGuardando(true);
      const res = await atencionesAPI.create(payload);
      if (!res.data.success) throw new Error(res.data.message || res.data.error || 'Error');
      const data = res.data.data;
      toast.success(`Atención ${data.atencion.codigo} registrada`);

      if (enviarEmail && emailDestino && emailDestino !== cliente?.email) {
        try { await comprobantesAtencionAPI.enviarEmail(data.comprobante.id, { destinatario: emailDestino }); }
        catch (e) {}
      }

      setTimeout(() => abrirPDF(data.comprobante.id), 400);
      if (onSuccess) onSuccess(data);
      resetForm();
      onClose(); // cierre tras exito (sin confirmacion porque el form ya se reseteo)
    } catch (e) {
      // FIX defensa en profundidad: capturar tanto data.error (formato apiResponse del backend)
      // como data.message, plus log enriquecido para diagnostico futuro de fallos en producción.
      const backendMsg = e.response?.data?.error || e.response?.data?.message;
      const httpStatus = e.response?.status;
      const networkErr = !e.response;
      console.error('[Cobrar atención] Error al registrar:', {
        status: httpStatus,
        backendMsg,
        networkErr,
        rawError: e.message,
        payload: { items_count: payload.items.length, total: totales.total, metodo: payload.metodo_pago, es_credito: payload.es_credito }
      });
      const userMsg = networkErr
        ? 'No se pudo conectar con el servidor. Verifica tu conexión.'
        : (backendMsg || e.message || 'Error al registrar atención');
      toast.error(userMsg);
    } finally {
      setGuardando(false);
    }
  };

  const resetForm = () => {
    setCarrito([]); setDescuentoGlobal(0); setMetodoPago('efectivo');
    setMontoRecibido(0); setPagoMixto([{ metodo: 'efectivo', monto: 0 }, { metodo: 'tarjeta', monto: 0 }]);
    setEsCredito(false); setAbonoInicial({ monto: 0, metodo_pago: 'efectivo' });
    setFechaVencimiento(''); setNotas(''); setNumeroOrden('');
    setTarjetaData({ codigo_autorizacion: '', ultimos_digitos: '', marca_tarjeta: '', tipo_tarjeta: 'credito' });
    setBusquedaServ(''); setCategoriaFiltro('');
    loadedRef.current = false;
  };

  // FIX: resetear siempre al cerrar. Pide confirmacion si hay datos capturados.
  const handleClose = () => {
    if (carrito.length > 0 && !guardando) {
      if (!window.confirm('Hay servicios en el carrito. ¿Descartar y cerrar?')) return;
    }
    resetForm();
    onClose();
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(21, 67, 96, 0.55)', backdropFilter: 'blur(2px)' }} onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content border-0" style={{ borderRadius: 10, boxShadow: '0 20px 60px rgba(21, 67, 96, 0.25)' }}>
          {/* HEADER minimalista */}
          <div className="modal-header border-0 px-4 py-3" style={{ background: BRAND.primary, color: 'white', borderRadius: '10px 10px 0 0' }}>
            <div>
              <h6 className="modal-title fw-semibold mb-0" style={{ fontSize: '0.95rem', letterSpacing: '0.3px' }}>
                <i className="bi bi-cash-stack me-2"></i>Cobrar atención
              </h6>
              {historiaClinicaId && <div style={{ fontSize: '0.7rem', opacity: 0.75, marginTop: 2 }}>Desde Historia Clínica</div>}
            </div>
            <button type="button" className="btn-close btn-close-white" onClick={handleClose}></button>
          </div>

          <div className="modal-body p-0">
            <div className="row g-0">
              {/* COLUMNA IZQUIERDA: catálogo */}
              <div className="col-lg-7" style={{ borderRight: `1px solid ${BRAND.border}`, minHeight: '70vh' }}>
                <div className="px-3 py-3" style={{ borderBottom: `1px solid ${BRAND.border}` }}>
                  <div className="d-flex gap-2 flex-wrap">
                    <div className="input-group flex-grow-1" style={{ maxWidth: 380 }}>
                      <span className="input-group-text bg-white border-end-0" style={{ borderColor: BRAND.border }}><i className="bi bi-search" style={{ color: BRAND.muted }}></i></span>
                      <input className="form-control border-start-0" placeholder="Buscar servicio..." value={busquedaServ} onChange={e => setBusquedaServ(e.target.value)} style={{ borderColor: BRAND.border }} />
                    </div>
                    <select className="form-select" style={{ maxWidth: 180, borderColor: BRAND.border }} value={categoriaFiltro} onChange={e => setCategoriaFiltro(e.target.value)}>
                      <option value="">Todas categorías</option>
                      {Object.entries(CAT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>

                <div className="p-3" style={{ maxHeight: '65vh', overflowY: 'auto', background: BRAND.bgSoft }}>
                  {loadingServicios ? (
                    <div className="text-center py-5"><div className="spinner-border" style={{ color: BRAND.primary, width: 36, height: 36 }}></div></div>
                  ) : servFiltrados.length === 0 ? (
                    <div className="text-center py-5" style={{ color: BRAND.muted }}>
                      <i className="bi bi-inbox" style={{ fontSize: 40 }}></i>
                      <p className="mt-2 mb-0 small">No hay servicios disponibles</p>
                    </div>
                  ) : (
                    <div className="row g-2">
                      {servFiltrados.map(s => (
                        <div key={s.id} className="col-12 col-md-6">
                          <button
                            type="button"
                            className="w-100 text-start p-3 d-flex align-items-center gap-3"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); agregarServicio(s); }}
                            style={{
                              background: 'white',
                              border: `1px solid ${BRAND.border}`,
                              borderRadius: 6,
                              transition: 'all .15s',
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = BRAND.primary; e.currentTarget.style.background = BRAND.bgSofter; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = BRAND.border; e.currentTarget.style.background = 'white'; }}
                          >
                            <div className="flex-grow-1 min-w-0">
                              <div className="fw-medium text-truncate" style={{ color: BRAND.text, fontSize: '0.9rem' }}>{s.nombre}</div>
                              <div style={{ color: BRAND.muted, fontSize: '0.72rem', marginTop: 2 }}>
                                {s.codigo} · {CAT_LABEL[s.categoria] || s.categoria}
                              </div>
                            </div>
                            <div className="fw-semibold" style={{ color: BRAND.primary, fontSize: '0.95rem', minWidth: 60, textAlign: 'right' }}>
                              ${parseFloat(s.precio).toFixed(2)}
                            </div>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* COLUMNA DERECHA: carrito + pago */}
              <div className="col-lg-5" style={{ background: 'white' }}>
                {/* Cliente */}
                <div className="px-3 py-3" style={{ borderBottom: `1px solid ${BRAND.border}` }}>
                  {cliente ? (
                    <div className="d-flex align-items-center gap-2 p-2" style={{ background: BRAND.bgSofter, borderRadius: 6 }}>
                      <div className="d-flex align-items-center justify-content-center rounded-circle" style={{ width: 36, height: 36, background: BRAND.primary, color: 'white', fontSize: '0.75rem', fontWeight: 600, flexShrink: 0 }}>
                        {(cliente.nombres?.[0] || 'C').toUpperCase()}{(cliente.apellidos?.[0] || '').toUpperCase()}
                      </div>
                      <div className="flex-grow-1 min-w-0">
                        <div className="small fw-medium text-truncate" style={{ color: BRAND.text }}>{cliente.razon_social || `${cliente.nombres || ''} ${cliente.apellidos || ''}`.trim()}</div>
                        <div className="text-truncate" style={{ color: BRAND.muted, fontSize: '0.72rem' }}>{cliente.identificacion}{cliente.email ? ` · ${cliente.email}` : ''}</div>
                      </div>
                      {!clientePreseleccionado && (
                        <button type="button" className="btn btn-sm btn-link p-0" style={{ color: BRAND.muted }} onClick={() => { setCliente(null); setEmailDestino(''); }} title="Quitar">
                          <i className="bi bi-x-lg"></i>
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="position-relative">
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-white" style={{ borderColor: BRAND.border, color: BRAND.muted }}><i className="bi bi-person"></i></span>
                        <input className="form-control" placeholder="Buscar paciente (opcional)" value={buscandoCliente} onChange={e => buscarClientes(e.target.value)} style={{ borderColor: BRAND.border }} />
                      </div>
                      {clientesEncontrados.length > 0 && (
                        <div className="position-absolute w-100 bg-white rounded mt-1" style={{ zIndex: 1050, border: `1px solid ${BRAND.border}`, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                          {clientesEncontrados.map(c => (
                            <button type="button" key={c.id} className="btn btn-light w-100 text-start border-0 rounded-0 py-2" onClick={() => seleccionarCliente(c)}>
                              <div className="small fw-medium" style={{ color: BRAND.text }}>{`${c.nombres || ''} ${c.apellidos || ''}`.trim() || c.razon_social}</div>
                              <div style={{ color: BRAND.muted, fontSize: '0.72rem' }}>{c.identificacion}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Carrito */}
                <div className="px-3 py-3" style={{ maxHeight: '32vh', overflowY: 'auto' }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ color: BRAND.muted, fontSize: '0.7rem', letterSpacing: '0.5px', fontWeight: 600 }}>SERVICIOS ({carrito.length})</span>
                    {carrito.length > 0 && <button type="button" className="btn btn-sm btn-link p-0" style={{ color: BRAND.muted, fontSize: '0.75rem' }} onClick={() => setCarrito([])}>Limpiar</button>}
                  </div>
                  {carrito.length === 0 ? (
                    <div className="text-center py-4" style={{ color: BRAND.muted }}>
                      <i className="bi bi-cart3" style={{ fontSize: 32 }}></i>
                      <p className="small mb-0 mt-2">Agrega servicios del catálogo</p>
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      {carrito.map((it, idx) => (
                        <div key={idx} className="p-2" style={{ background: BRAND.bgSoft, borderRadius: 6, border: `1px solid ${BRAND.border}` }}>
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <div className="flex-grow-1 min-w-0">
                              <div className="small fw-medium text-truncate" style={{ color: BRAND.text }}>{it.nombre}</div>
                              <div style={{ color: BRAND.muted, fontSize: '0.7rem' }}>{it.codigo}</div>
                            </div>
                            <button type="button" className="btn btn-sm btn-link p-0" style={{ color: BRAND.danger }} onClick={() => removerItem(idx)}>
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </div>
                          <div className="d-flex gap-1 align-items-center">
                            <div className="input-group input-group-sm" style={{ maxWidth: 90 }}>
                              <button type="button" className="btn btn-outline-secondary" style={{ borderColor: BRAND.border }} onClick={() => actualizarItem(idx, 'cantidad', Math.max(1, it.cantidad - 1))}>−</button>
                              <input type="number" className="form-control text-center" value={it.cantidad} min="1" {...moneyInputProps} onChange={e => actualizarItem(idx, 'cantidad', Math.max(1, parseInt(e.target.value || '1')))} style={{ borderColor: BRAND.border }} />
                              <button type="button" className="btn btn-outline-secondary" style={{ borderColor: BRAND.border }} onClick={() => actualizarItem(idx, 'cantidad', it.cantidad + 1)}>+</button>
                            </div>
                            <div className="input-group input-group-sm flex-grow-1">
                              <span className="input-group-text bg-white" style={{ borderColor: BRAND.border, color: BRAND.muted }}>$</span>
                              <input type="number" className="form-control" value={it.precio_unitario} step="0.01" min="0" {...moneyInputProps} onChange={e => actualizarItem(idx, 'precio_unitario', parseFloat(e.target.value) || 0)} style={{ borderColor: BRAND.border }} />
                            </div>
                            <div className="fw-semibold small" style={{ minWidth: 60, textAlign: 'right', color: BRAND.primary }}>
                              ${((it.cantidad * it.precio_unitario) * (1 - (it.descuento_porcentaje || 0) / 100)).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Totales + pago */}
                <div className="px-3 py-3" style={{ borderTop: `1px solid ${BRAND.border}` }}>
                  <div className="d-flex justify-content-between align-items-center mb-1" style={{ color: BRAND.muted, fontSize: '0.85rem' }}>
                    <span>Subtotal</span><span>${totales.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ color: BRAND.muted, fontSize: '0.85rem' }}>Descuento</span>
                    <div className="input-group input-group-sm" style={{ maxWidth: 120 }}>
                      <span className="input-group-text bg-white" style={{ borderColor: BRAND.border, color: BRAND.muted }}>$</span>
                      <input type="number" min="0" step="0.01" className="form-control" value={descuentoGlobal} {...moneyInputProps} onChange={e => setDescuentoGlobal(e.target.value)} style={{ borderColor: BRAND.border }} />
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center py-2 mb-3" style={{ borderTop: `1px solid ${BRAND.border}`, borderBottom: `1px solid ${BRAND.border}` }}>
                    <span style={{ color: BRAND.text, fontWeight: 600, letterSpacing: '0.5px', fontSize: '0.85rem' }}>TOTAL</span>
                    <span style={{ color: BRAND.primary, fontWeight: 700, fontSize: '1.5rem' }}>${totales.total.toFixed(2)}</span>
                  </div>

                  {/* [N° orden física] Arriba de todo para que se vea sin scroll al facturar */}
                  <div className="mb-2">
                    <div style={{ color: BRAND.muted, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px', marginBottom: 4 }}>N° ORDEN FÍSICA (OPCIONAL)</div>
                    <input className="form-control form-control-sm" placeholder="Código de la orden de papel" maxLength={50}
                      value={numeroOrden} onChange={e => setNumeroOrden(e.target.value)} style={{ borderColor: BRAND.border }} />
                  </div>

                  {/* Métodos de pago */}
                  <div className="mb-3">
                    <div style={{ color: BRAND.muted, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px', marginBottom: 6 }}>MÉTODO DE PAGO</div>
                    <div className="d-flex gap-1 flex-wrap">
                      {METODOS.map(m => {
                        const active = (!esCredito && metodoPago === m.id) || (esCredito && m.id === 'credito');
                        return (
                          <button key={m.id} type="button"
                            className="btn btn-sm flex-grow-1"
                            style={{
                              background: active ? BRAND.primary : 'white',
                              color: active ? 'white' : BRAND.text,
                              border: `1px solid ${active ? BRAND.primary : BRAND.border}`,
                              fontSize: '0.7rem',
                              padding: '6px 4px',
                              minWidth: 70
                            }}
                            onClick={() => {
                              if (m.id === 'credito') { setEsCredito(true); setMetodoPago('credito'); }
                              else { setEsCredito(false); setMetodoPago(m.id); }
                              // El useEffect cercano a `cambio` se encarga del auto-fill de montoRecibido
                              // cuando se selecciona efectivo (centraliza la logica para cualquier evento).
                            }}>
                            <i className={`${m.icon} d-block mb-1`} style={{ fontSize: '1rem' }}></i>
                            {m.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {!esCredito && metodoPago === 'efectivo' && (
                    <div className="row g-2 mb-2">
                      <div className="col-7">
                        <label className="small mb-1" style={{ color: BRAND.muted, fontSize: '0.72rem' }}>Monto recibido</label>
                        <div className="input-group input-group-sm">
                          <span className="input-group-text bg-white" style={{ borderColor: BRAND.border, color: BRAND.muted }}>$</span>
                          <input type="number" min="0" step="0.01" className="form-control" value={montoRecibido} {...moneyInputProps} onChange={e => setMontoRecibido(e.target.value)} style={{ borderColor: BRAND.border }} />
                        </div>
                      </div>
                      <div className="col-5">
                        <label className="small mb-1" style={{ color: BRAND.muted, fontSize: '0.72rem' }}>Cambio</label>
                        <div className="form-control form-control-sm fw-semibold" style={{ background: BRAND.bgSoft, color: cambio > 0 ? BRAND.success : BRAND.muted, borderColor: BRAND.border }}>
                          ${cambio.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  )}

                  {!esCredito && metodoPago === 'tarjeta' && (
                    <div className="row g-2 mb-2">
                      <div className="col-6"><input className="form-control form-control-sm" placeholder="Cód. autorización" value={tarjetaData.codigo_autorizacion} onChange={e => setTarjetaData({ ...tarjetaData, codigo_autorizacion: e.target.value })} style={{ borderColor: BRAND.border }} /></div>
                      <div className="col-6"><input className="form-control form-control-sm" placeholder="Últimos 4" maxLength="4" value={tarjetaData.ultimos_digitos} onChange={e => setTarjetaData({ ...tarjetaData, ultimos_digitos: e.target.value })} style={{ borderColor: BRAND.border }} /></div>
                      <div className="col-6"><input className="form-control form-control-sm" placeholder="Marca (VISA, MC...)" value={tarjetaData.marca_tarjeta} onChange={e => setTarjetaData({ ...tarjetaData, marca_tarjeta: e.target.value })} style={{ borderColor: BRAND.border }} /></div>
                      <div className="col-6">
                        <select className="form-select form-select-sm" value={tarjetaData.tipo_tarjeta} onChange={e => setTarjetaData({ ...tarjetaData, tipo_tarjeta: e.target.value })} style={{ borderColor: BRAND.border }}>
                          <option value="credito">Crédito</option>
                          <option value="debito">Débito</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {!esCredito && metodoPago === 'mixto' && (
                    <div className="mb-2">
                      {pagoMixto.map((p, i) => (
                        <div key={i} className="input-group input-group-sm mb-1">
                          <select className="form-select" style={{ maxWidth: 120, borderColor: BRAND.border }} value={p.metodo} onChange={e => setPagoMixto(pagoMixto.map((x, j) => j === i ? { ...x, metodo: e.target.value } : x))}>
                            <option value="efectivo">Efectivo</option>
                            <option value="tarjeta">Tarjeta</option>
                            <option value="transferencia">Transf.</option>
                          </select>
                          <span className="input-group-text bg-white" style={{ borderColor: BRAND.border, color: BRAND.muted }}>$</span>
                          <input type="number" min="0" step="0.01" className="form-control" value={p.monto} {...moneyInputProps} onChange={e => setPagoMixto(pagoMixto.map((x, j) => j === i ? { ...x, monto: e.target.value } : x))} style={{ borderColor: BRAND.border }} />
                        </div>
                      ))}
                      <div className="small text-end" style={{ color: BRAND.muted }}>
                        Suma: <span className="fw-medium" style={{ color: BRAND.text }}>${pagoMixto.reduce((s, p) => s + parseFloat(p.monto || 0), 0).toFixed(2)}</span> / ${totales.total.toFixed(2)}
                      </div>
                    </div>
                  )}

                  {esCredito && (
                    <div className="mb-2 p-2" style={{ background: '#FFF9E6', border: `1px solid ${BRAND.warning}40`, borderRadius: 6 }}>
                      <div className="small fw-medium mb-2" style={{ color: '#8B6D00' }}>Abono inicial requerido</div>
                      <div className="row g-2">
                        <div className="col-7">
                          <div className="input-group input-group-sm">
                            <span className="input-group-text bg-white" style={{ borderColor: BRAND.border, color: BRAND.muted }}>$</span>
                            <input type="number" min="0.01" step="0.01" className="form-control" placeholder="Monto" value={abonoInicial.monto} {...moneyInputProps} onChange={e => setAbonoInicial({ ...abonoInicial, monto: e.target.value })} style={{ borderColor: BRAND.border }} />
                          </div>
                        </div>
                        <div className="col-5">
                          <select className="form-select form-select-sm" value={abonoInicial.metodo_pago} onChange={e => setAbonoInicial({ ...abonoInicial, metodo_pago: e.target.value })} style={{ borderColor: BRAND.border }}>
                            <option value="efectivo">Efectivo</option>
                            <option value="tarjeta">Tarjeta</option>
                            <option value="transferencia">Transferencia</option>
                          </select>
                        </div>
                        <div className="col-12">
                          <input type="date" className="form-control form-control-sm" value={fechaVencimiento} onChange={e => setFechaVencimiento(e.target.value)} style={{ borderColor: BRAND.border }} />
                        </div>
                      </div>
                      <div className="small mt-2" style={{ color: BRAND.muted }}>Saldo pendiente: <span className="fw-medium" style={{ color: BRAND.text }}>${Math.max(0, totales.total - parseFloat(abonoInicial.monto || 0)).toFixed(2)}</span></div>
                    </div>
                  )}

                  <div className="mb-2">
                    <input className="form-control form-control-sm" placeholder="Notas (opcional)" value={notas} onChange={e => setNotas(e.target.value)} style={{ borderColor: BRAND.border }} />
                  </div>

                  <div className="d-flex gap-2 align-items-center mb-3">
                    <div className="form-check m-0 flex-shrink-0">
                      <input type="checkbox" id="enviar-email-cbx" className="form-check-input" checked={enviarEmail} onChange={e => setEnviarEmail(e.target.checked)} />
                      <label htmlFor="enviar-email-cbx" className="form-check-label small" style={{ color: BRAND.text }}>Email</label>
                    </div>
                    <input type="email" className="form-control form-control-sm" placeholder="correo@ejemplo.com" value={emailDestino} onChange={e => setEmailDestino(e.target.value)} disabled={!enviarEmail} style={{ borderColor: BRAND.border }} />
                  </div>

                  <button
                    type="button"
                    className="btn w-100 fw-semibold py-2"
                    disabled={guardando || carrito.length === 0 || totales.total <= 0}
                    onClick={handleSubmit}
                    style={{
                      background: guardando || carrito.length === 0 ? '#BDC3C7' : BRAND.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      letterSpacing: '0.5px',
                      fontSize: '0.9rem'
                    }}
                  >
                    {guardando ? (
                      <><span className="spinner-border spinner-border-sm me-2"></span>Procesando...</>
                    ) : (
                      <><i className="bi bi-check2 me-2"></i>COBRAR ${totales.total.toFixed(2)}</>
                    )}
                  </button>
                  {/* Helper visible: explica por que el boton esta disabled (UX defensiva). */}
                  {!guardando && (carrito.length === 0 || totales.total <= 0) && (
                    <div className="text-center mt-2" style={{ color: BRAND.muted, fontSize: '0.72rem' }}>
                      {carrito.length === 0
                        ? <><i className="bi bi-info-circle me-1"></i>Agrega al menos un servicio del catálogo</>
                        : <><i className="bi bi-info-circle me-1"></i>El total debe ser mayor a $0.00</>
                      }
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalCobrarAtencion;
